/**
 * EARNINGS & DISTRIBUTIONS (Phase 2). What the portfolio is paying out: rent, profit share,
 * distributions, interest. Read-only reporting plus a withdraw action for what has settled.
 */

import { fmtDateShort } from '@/lib/format';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { StatTile as Stat } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';

const BAR: Record<string, string> = {
  emerald: 'bg-emerald-500',
  lime: 'bg-lime-500',
  amber: 'bg-amber-500',
  violet: 'bg-violet-500',
  sky: 'bg-sky-500',
  teal: 'bg-teal-500',
};

export default function Earnings() {
  const { data, loading, error, reload } = useResource(() => api.earnings(), []);

  if (loading)
    return (
      <div className="space-y-6">
        <LoadingCard lines={3} />
        <LoadingCard lines={4} />
      </div>
    );
  if (error) return <ErrorState body="We could not load your earnings." onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-page-title text-ink">Earnings</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Income from your investments.</p>
      </header>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        {/* MAIN */}
        <div className="space-y-6 lg:col-span-2">
          {/* AVAILABLE TO WITHDRAW HERO */}
          <Reveal>
            <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
              />
              <div className="relative flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-sm text-white/70">Available to withdraw</p>
                  <p className="mt-1 flex items-baseline gap-2">
                    <span className="text-sm font-medium text-white/70">KES</span>
                    <span className="tabular text-4xl font-semibold tracking-tight">
                      {data.available}
                    </span>
                  </p>
                  <p className="mt-1 text-sm text-white/70">
                    KES {data.totalEarned} earned in total
                  </p>
                </div>
                {/* This carried no handler at all: the one call to action on the earnings screen did
                    nothing when pressed. It now opens the withdraw flow (FR-U-WAL-006) prefilled
                    with what has settled, and the flow does the rest -- the caps, the verified
                    destination and the escrow all belong there, not here. The comma is stripped
                    because the fixture formats for display and the API takes a decimal string. */}
                <Link
                  to={`/wallet/withdraw?source=earnings&amount=${encodeURIComponent(
                    data.available.replace(/,/g, ''),
                  )}`}
                  className="rounded-field bg-white px-4 py-2.5 text-sm font-semibold text-primary shadow-soft transition hover:bg-white/90"
                >
                  Withdraw
                </Link>
              </div>
            </section>
          </Reveal>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="This month" value={`KES ${data.thisMonth}`} accent />
            <Stat label="Projected / yr" value={`KES ${data.projectedAnnual}`} />
            <Stat label="Total earned" value={`KES ${data.totalEarned}`} />
          </div>

          {/* DISTRIBUTION HISTORY */}
          <section>
            <h2 className="mb-2 font-semibold text-ink">Distribution history</h2>
            <Stagger className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {data.distributions.map((d, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/12 text-accent">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M12 3v13m0 0 4-4m-4 4-4-4M5 21h14"
                        />
                      </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{d.kind}</p>
                      <p className="truncate text-xs text-ink-subtle">
                        {d.source} &middot; {fmtDateShort(d.at)}
                      </p>
                    </div>
                    <span className="tabular shrink-0 text-sm font-semibold text-accent">
                      + KES {d.amount}
                    </span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </section>
        </div>

        {/* RAIL: by type + upcoming + tax */}
        <div className="space-y-6">
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">By type</h2>
            <div className="mt-3 flex h-2.5 overflow-hidden rounded-full">
              {data.byType.map((t) => (
                <div
                  key={t.type}
                  className={BAR[t.tone] ?? 'bg-primary'}
                  style={{ width: `${t.pct}%` }}
                  title={`${t.type} ${t.pct}%`}
                />
              ))}
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {data.byType.map((t) => (
                <li key={t.type} className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-ink-muted">
                    <span className={`h-2.5 w-2.5 rounded-sm ${BAR[t.tone] ?? 'bg-primary'}`} />
                    {t.type}
                  </span>
                  <span className="tabular font-medium text-ink">KES {t.amount}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Upcoming</h2>
            <ul className="mt-3 space-y-3">
              {data.upcoming.map((u, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{u.kind}</p>
                    <p className="truncate text-xs text-ink-subtle">
                      {u.source} &middot; due {fmtDateShort(u.dueDate)}
                    </p>
                  </div>
                  <span className="tabular shrink-0 text-sm font-medium text-ink-muted">
                    ~ KES {u.estAmount}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <Link
            to="/portfolio/tax"
            className="flex w-full items-center justify-center rounded-field border border-border bg-surface py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Tax statements
          </Link>
        </div>
      </div>
    </div>
  );
}
